import { Zap, Landmark, Building2, Home, Shield, ChevronDown, ChevronUp, Target, AlertTriangle, Scale, FileCheck } from 'lucide-react';

export const SECTOR_PROFILES = [
  {
    id: 'energy',
    name: 'Energy Sector',
    code: 'EN',
    icon: Zap,
    accentColor: '#E67E22',
    description: 'AI-powered analytics for generation mix transition, renewable project finance, and decarbonization pathways across utilities, IPPs, and O&G majors.',
    targetArchetypes: [
      'Integrated Utilities ($5B-$50B)',
      'IPP / Independent Generators ($500M-$10B)',
      'Oil & Gas Majors ($50B-$400B)',
    ],
    totalWorkflows: 10,
    keyRegulations: ['EU Fit for 55', 'US IRA', 'CSRD', 'SEC Climate', 'OGMP 2.0'],
    headlineMetric: { label: 'Stranded Asset Risk', value: '$1.2T', sub: 'by 2036' },
    workflows: [
      { id: 'EN-01', name: 'Generation Mix Transition Planning', painPoint: 'Stranded Asset Risk ($1.2T risk by 2036)', outcome: 'LCOE reduction 5-15%, Stranded exposure reduction 20-40%', regulators: ['EU Fit for 55', 'US IRA', 'CSRD'] },
      { id: 'EN-02', name: 'Renewable Project Finance (P50/P90)', painPoint: 'Energy Yield Uncertainty (5-15%)', outcome: 'DSCR Improvement 0.1-0.3x, Equity IRR +0.5-2.0%', regulators: ['IRA Tax Credits', 'EU Taxonomy'] },
      { id: 'EN-05', name: 'Methane Monitoring (OGMP 2.0)', painPoint: 'Measurement Uncertainty (50-200%)', outcome: 'Methane Reduction 20-50%, Product Recovery 10-30%', regulators: ['OGMP 2.0', 'EPA OOOOa', 'Global Methane Pledge'] },
      { id: 'EN-09', name: 'Scope 3 Category 11 (End-Use Combustion)', painPoint: 'Magnitude: 80-95% of O&G emissions', outcome: 'Reporting cost reduction 30-50%, ESG rating +1-2 notches', regulators: ['SEC Climate', 'EU CSRD', 'GHG Protocol'] },
      { id: 'EN-10', name: 'CSRD E1-E5 Complete Disclosure', painPoint: 'Complexity: 12 ESRS standards', outcome: 'Compliance cost reduction 40-60%, Audit efficiency +30-50%', regulators: ['CSRD', 'ESRS E1-E5'] },
    ],
  },
  {
    id: 'vcpe',
    name: 'Venture Capital / Private Equity',
    code: 'PE',
    icon: Target,
    accentColor: '#9B59B6',
    description: 'ESG-integrated deal screening, due diligence, portfolio monitoring, and LP reporting for buyout, growth, and venture capital firms.',
    targetArchetypes: [
      'Large Buyout Funds ($10B-$100B AUM)',
      'Growth Equity ($1B-$10B AUM)',
      'Venture Capital ($500M-$5B AUM)',
    ],
    totalWorkflows: 10,
    keyRegulations: ['SFDR Art. 8/9', 'SEC Climate Rules', 'EU Taxonomy', 'ILPA Principles'],
    headlineMetric: { label: 'Write-off Reduction', value: '10-30%', sub: 'via ESG screening' },
    workflows: [
      { id: 'PE-01', name: 'Deal Screening ESG Risk Assessment', painPoint: 'Speed vs. Thoroughness (2-4 week timelines)', outcome: 'Write-off Reduction 10-30%, Screening efficiency +50-70%', regulators: ['SFDR', 'SEC Climate', 'EU Taxonomy'] },
      { id: 'PE-04', name: 'Value Creation Plan (ESG)', painPoint: 'Opportunity Identification & Measurement', outcome: 'EBITDA Impact 2-5%, Revenue Growth 5-15%', regulators: ['SFDR', 'EU Taxonomy'] },
      { id: 'PE-05', name: 'Exit Readiness Scoring', painPoint: 'Buyer ESG Scrutiny & Disclosure Requirements', outcome: 'Valuation Protection 5-15%, Exit 3-6 months faster', regulators: ['SEC Climate', 'EU CSRD', 'SFDR'] },
      { id: 'PE-08', name: 'IRR Sensitivity to ESG Factors', painPoint: 'Factor Identification & Quantification', outcome: 'IRR Adjustment 0.5-2.0%, Capital access +10-30 bps', regulators: ['SEC Climate', 'SFDR PAI', 'TCFD'] },
      { id: 'PE-09', name: 'Fund-Level Carbon Footprint', painPoint: 'Data Availability & Attribution', outcome: 'Fundraising premium 10-25%, Compliance automation 30-50%', regulators: ['PCAF', 'SFDR PAI', 'NZBA'] },
    ],
  },
  {
    id: 'asset-mgmt',
    name: 'Asset Managers',
    code: 'AM',
    icon: Scale,
    accentColor: '#3498DB',
    description: 'Portfolio construction with ESG constraints, SFDR periodic reporting, attribution analysis, and net-zero alignment tracking for institutional managers.',
    targetArchetypes: [
      'Large Institutional ($500B-$3T AUM)',
      'ESG Specialist ($10B-$100B AUM)',
      'Regional Manager ($50B-$500B AUM)',
    ],
    totalWorkflows: 10,
    keyRegulations: ['SFDR Art. 8/9', 'EU Taxonomy', 'SEC Climate', 'TCFD', 'NZAM'],
    headlineMetric: { label: 'Alpha Generation', value: '0.2-0.5%', sub: 'ESG-adjusted returns' },
    workflows: [
      { id: 'AM-01', name: 'Portfolio Construction with ESG Constraints', painPoint: 'Constraint Complexity & Tracking Error', outcome: 'Tracking error reduction 10-30 bps, Flow generation +15-30%', regulators: ['SFDR Art. 8/9', 'EU Taxonomy'] },
      { id: 'AM-02', name: 'SFDR Periodic Reporting (Art. 8/9)', painPoint: 'Data Volume (18 PAI mandatory indicators)', outcome: 'Reporting automation savings 50-70%, Penalty avoidance $5-50M', regulators: ['SFDR', 'EU Taxonomy', 'ESMA'] },
      { id: 'AM-03', name: 'ESG Attribution Analysis', painPoint: 'Factor Definition & Attribution Model', outcome: 'Client retention uplift, Alpha generation 0.2-0.5%', regulators: ['SFDR', 'SEC Marketing Rule', 'GIPS'] },
      { id: 'AM-05', name: 'Stewardship & Voting Outcomes', painPoint: 'Scale Challenge & Effectiveness Measurement', outcome: 'Engagement efficiency +20-40%, Voting impact +10-25%', regulators: ['UK Stewardship Code', 'EU SRD', 'PRI'] },
      { id: 'AM-09', name: 'Paris Alignment & Net Zero Tracker', painPoint: 'Methodology Selection (TPI, PACTA)', outcome: 'Client retention uplift, Flow generation +15-30%', regulators: ['TCFD', 'SFDR', 'NZAM/NZAOA'] },
    ],
  },
  {
    id: 'real-estate',
    name: 'Real Estate / REITs',
    code: 'RE',
    icon: Home,
    accentColor: '#2ECC71',
    description: 'Portfolio valuation with ESG integration, CRREM pathway alignment, physical risk mapping, and GRESB submission workflows for global portfolios.',
    targetArchetypes: [
      'Global REITs ($10B-$100B)',
      'Institutional Funds ($5B-$50B)',
      'Regional Owners ($1B-$10B)',
    ],
    totalWorkflows: 10,
    keyRegulations: ['CRREM', 'EU MEPS', 'GRESB', 'TCFD', 'EU Taxonomy'],
    headlineMetric: { label: 'Valuation Uplift', value: '2-5%', sub: 'ESG-adjusted NAV' },
    workflows: [
      { id: 'RE-01', name: 'Portfolio Valuation Roll-Up', painPoint: 'Data Aggregation & ESG Integration', outcome: 'Valuation accuracy +2-5%, Cycle time reduction 30-50%', regulators: ['IFRS 13', 'EU Taxonomy', 'GRESB'] },
      { id: 'RE-02', name: 'CRREM Pathway Alignment', painPoint: 'Pathway Complexity & Stranding Risk', outcome: 'Stranding risk ID 10-30%, Retrofit ROI +15-25%', regulators: ['CRREM', 'EU MEPS', 'EPBD'] },
      { id: 'RE-04', name: 'Physical Risk Mapping (Flood/Heat/Fire)', painPoint: 'Data Granularity & Scenario Selection', outcome: 'Insurance optimization 10-25%, Damage cost avoidance 20-40%', regulators: ['TCFD', 'SEC Climate', 'EU Taxonomy'] },
      { id: 'RE-06', name: 'GRESB Submission Workflow', painPoint: 'Data Collection & Timeline Pressure', outcome: 'Score improvement 5-15 points, Submission cost reduction 30-50%', regulators: ['GRESB', 'EU Taxonomy', 'SFDR'] },
      { id: 'RE-07', name: 'Green Premium / Brown Discount', painPoint: 'Data Availability & Methodology', outcome: 'Investment alpha 0.5-2.0% IRR, Valuation accuracy +2-5%', regulators: ['IFRS 13', 'RICS', 'EU Taxonomy'] },
    ],
  },
  {
    id: 'financial',
    name: 'Financial Institutions',
    code: 'FI',
    icon: Shield,
    accentColor: '#E74C3C',
    description: 'Loan book climate stress testing, PCAF financed emissions, green asset ratio calculation, and counterparty climate risk scoring for banks and insurers.',
    targetArchetypes: [
      'Global Systemic Banks ($1T-$3T assets)',
      'Regional Banks ($100B-$500B assets)',
      'Insurers / Reinsurers ($50B-$1T assets)',
    ],
    totalWorkflows: 10,
    keyRegulations: ['ECB Climate Stress Test', 'PCAF', 'EBA Pillar 3', 'Solvency II', 'IFRS 9'],
    headlineMetric: { label: 'Capital Efficiency', value: '5-15%', sub: 'climate-adjusted' },
    workflows: [
      { id: 'FI-01', name: 'Loan Book Climate Stress Test', painPoint: 'Scenario Complexity & Data Availability', outcome: 'Capital efficiency 5-15%, Risk mitigation 10-25%', regulators: ['ECB Climate Stress Test', 'Fed Scenario Analysis'] },
      { id: 'FI-02', name: 'PCAF Financed Emissions Report', painPoint: 'Data Quality & Attribution', outcome: 'Reporting automation 40-60%, Capital markets benefit 10-30 bps', regulators: ['PCAF', 'SEC Climate', 'SFDR PAI'] },
      { id: 'FI-03', name: 'Green Asset Ratio (GAR) Calculation', painPoint: 'Technical Complexity (EU Taxonomy TSC)', outcome: 'Compliance cost reduction, Green origination +15-30%', regulators: ['EU Taxonomy Art. 449a', 'EBA ITS'] },
      { id: 'FI-05', name: 'Counterparty Climate Risk Scoring', painPoint: 'Scoring Consistency & Integration', outcome: 'Risk mitigation 10-25%, Pricing optimization 10-30 bps', regulators: ['ECB Guide on Climate Risks', 'EBA Guidelines'] },
      { id: 'FI-08', name: 'Insurance Solvency II / Cat Risk', painPoint: 'Model Complexity & Climate Integration', outcome: 'Capital efficiency 5-15%, Pricing accuracy +5-15%', regulators: ['Solvency II', 'EIOPA', 'IFRS 17'] },
    ],
  },
];

export { ChevronDown, ChevronUp };
